import React, { useEffect, useState } from "react";
import Select from "react-dropdown-select";
import { FiUploadCloud } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import "./SheetBulkUploadPopup.scss";
import { GetSheetBulkUploadDataRequest } from "../../../Redux/Slice";
import {
  ClientBulkUploadSampleSheetApi,
  DesignationBulkUploadApi,
  DesignationBulkUploadSampleSheetApi,
  SheetBulkUploadApi,
  organization_listapi,
} from "../../../text/apidata";

// Sheet Upload popup: only two upload types - Designation and Client
const DESIGNATION_COLUMNS = [
  // "Project Name",
  "Branch Name",
  "Designation",
  "From Salary",
  "To Salary",
  "Salary",
  "Wages",
];

const typeOptions = [
  { value: "designation", label: "Designation" },
  { value: "client", label: "Client" },
];

const allowedTypes = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const MAX_SIZE = 250 * 1024 * 1024; // 250MB

const SheetUploadPopup = ({ setPopup, reportType }) => {
  const dispatch = useDispatch();
  const userSessionData = useSelector((state) => state?.users?.UserData);
  const authHeader = { Authorization: "Bearer " + userSessionData?.session?.token };

  const [fileType, setFileType] = useState([typeOptions[0]]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [organization, setOrganization] = useState([]);
  const [file, setFile] = useState(null);
  // result of the client side check: { rows, missing }
  const [fileCheck, setFileCheck] = useState(null);
  const [sampleColumns, setSampleColumns] = useState([]);
  // one example row (same order as sampleColumns), written under the header in the sample sheet
  const [sampleRow, setSampleRow] = useState([]);
  const [busy, setBusy] = useState(false);

  const type = fileType?.[0]?.value;
  const org = organization?.[0];

  // Organisations are needed only for the Client upload (sample columns differ per organisation)
  useEffect(() => {
    if (type !== "client" || organizationOptions.length > 0) return;
    axios({ method: "get", url: organization_listapi, headers: authHeader })
      .then((response) => {
        const list = Array.isArray(response?.data?.data) ? response.data.data : [];
        setOrganizationOptions(
          list.map((o) => ({ value: o.id, label: o.organization_name }))
        );
      })
      .catch(() => toast.error("Failed to load organisations"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Columns the sheet must have: fixed for Designation, from the backend for Client
  useEffect(() => {
    setFile(null);
    setFileCheck(null);
    setSampleColumns([]);
    setSampleRow([]);
    if (type === "designation") {
      axios({ method: "get", url: DesignationBulkUploadSampleSheetApi, headers: authHeader })
        .then((response) => {
          const columns = response?.data?.data;
          const row = response?.data?.details;
          setSampleColumns(Array.isArray(columns) ? columns : DESIGNATION_COLUMNS);
          setSampleRow(Array.isArray(row) ? row : []);
        })
        .catch(() => setSampleColumns(DESIGNATION_COLUMNS));
      return;
    }
    if (!org?.value) return;
    axios({
      method: "get",
      url: ClientBulkUploadSampleSheetApi,
      params: { organization_id: org.value },
      headers: authHeader,
    })
      .then((response) => {
        const columns = response?.data?.data;
        const row = response?.data?.details;
        setSampleColumns(Array.isArray(columns) ? columns : []);
        setSampleRow(Array.isArray(row) ? row : []);
      })
      .catch(() => toast.error("Failed to load sample sheet columns"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, org?.value]);

  // Re-run the column check when the sample columns arrive after the file was picked
  useEffect(() => {
    if (file) checkFile(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleColumns]);

  const checkFile = (uploadedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const header = (rows[0] || []).map((c) => String(c).trim());
        setFileCheck({
          rows: Math.max(rows.length - 1, 0),
          missing: sampleColumns.filter((c) => !header.includes(c)),
        });
      } catch (err) {
        setFileCheck(null);
      }
    };
    reader.readAsBinaryString(uploadedFile);
  };

  const validateFile = (uploadedFile) => {
    if (!uploadedFile) return false;
    if (!allowedTypes.includes(uploadedFile.type)) {
      toast.error("Only Excel files (.xls, .xlsx) are allowed");
      return false;
    }
    if (uploadedFile.size > MAX_SIZE) {
      toast.error("File size should be less than 250MB");
      return false;
    }
    return true;
  };

  const pickFile = (uploadedFile) => {
    if (!validateFile(uploadedFile)) return;
    setFile(uploadedFile);
    checkFile(uploadedFile);
  };

  const downloadSample = () => {
    if (type === "client" && !org?.value) {
      toast.error("Please select an organisation");
      return;
    }
    if (sampleColumns.length === 0) {
      toast.error("No sample columns found");
      return;
    }
    const ws = XLSX.utils.aoa_to_sheet(sampleRow.length ? [sampleColumns, sampleRow] : [sampleColumns]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample");
    XLSX.writeFile(
      wb,
      type === "client" ? `Client_${org.label}_Sample.xlsx` : "Designation_Sample.xlsx"
    );
  };

  const uploadFile = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    if (type === "client" && !org?.value) {
      toast.error("Please select an organisation");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    if (type === "client") {
      formData.append("report_type", "0");
      formData.append("organization_id", org.value);
    }
    setBusy(true);
    try {
      const response = await axios({
        method: "post",
        url: type === "client" ? SheetBulkUploadApi : DesignationBulkUploadApi,
        data: formData,
        headers: authHeader,
      });
      toast.success(
        response?.data?.status?.message || response?.data?.message || "Upload started"
      );
      // refresh the history with the filter currently selected on the Sheet Upload tab
      dispatch(
        GetSheetBulkUploadDataRequest({
          search: "",
          page_size: 20,
          page: 1,
          ...(reportType ? { report_type: reportType } : {}),
        })
      );
      setPopup(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.status?.message ||
          error?.response?.data?.message ||
          "Upload failed"
      );
    } finally {
      setBusy(false);
    }
  };

  // Designation sheets with a wrong header are blocked; Client only warns (optional columns exist per organisation)
  const missing = fileCheck?.missing || [];
  const blocked = type === "designation" && missing.length > 0;

  return (
    <div className="overlay">
      <div className="popupContainers">
        <div className="popupHeader">
          <div />
          <span className="TitleHeader">Sheet Upload</span>
          <button className="closeButton" onClick={() => setPopup(false)}>
            ×
          </button>
        </div>

        <div className="uploadTypeHeader">
          <Select
            className="Selects"
            options={typeOptions}
            values={fileType}
            onChange={(value) => value.length && setFileType(value)}
            style={{ width: "480px", marginBottom: "0px" }}
            dropdownStyle={{ width: "480px" }}
          />
          <button className="sampleSheetBtn" onClick={downloadSample}>
            Sample Sheet
          </button>
        </div>

        {type === "client" && (
          <div className="uploadTypeHeader">
            <Select
              className="Selects"
              placeholder="Select organisation"
              options={organizationOptions}
              values={organization}
              onChange={(value) => setOrganization(value)}
              style={{ width: "480px", marginBottom: "0px" }}
              dropdownStyle={{ width: "480px" }}
            />
          </div>
        )}

        {/* Columns line hidden on request (kept for later)
        {sampleColumns.length > 0 && (
          <p className="subText sheetColumns">
            Columns: {sampleColumns.join(", ")}
          </p>
        )} */}

        <div
          className="uploadBox"
          onDrop={(e) => {
            e.preventDefault();
            pickFile(e.dataTransfer.files[0]);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          <FiUploadCloud className="uploadIcon" />
          <p className="mainText">Click to select or drag & drop your Excel file</p>
          <p className="subText">Supported formats: .xls, .xlsx</p>
          <p className="subText">Max file size 250MB</p>
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={(e) => pickFile(e.target.files[0])}
          />
          {file && <p className="fileName">{file.name}</p>}
        </div>

        {file && fileCheck && (
          <p className={missing.length ? "checkBad" : "checkOk"}>
            {fileCheck.rows} rows.{" "}
            {missing.length
              ? `Missing columns: ${missing.join(", ")}`
              : "Columns OK"}
          </p>
        )}

        <button
          className="submitBtn"
          disabled={!file || busy || blocked}
          onClick={uploadFile}
        >
          {busy ? "Uploading..." : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default SheetUploadPopup;
